import * as React from 'react';
import styled from 'styled-components';
import type { FlexAlignValue, Sizing, Sizing2, SizingAny } from './types';
import { resolveSizing } from './utils';

type DisplayValue =
  | 'block'
  | 'inline'
  | 'flex'
  | 'flow-root'
  | 'grid'
  | 'table'
  | 'list-tem'
  | 'inline list-tem'
  | 'table-row-group'
  | 'table-header-group'
  | 'table-footer-group'
  | 'table-row'
  | 'table-cell'
  | 'table-column-group'
  | 'table-column'
  | 'table-caption'
  | 'contents'
  | 'none'
  | 'inline-block'
  | 'inline-flex'
  | 'inline-grid'
  | 'inline-table'
  | 'block flow'
  | 'inline flow'
  | 'inline flow-root'
  | 'block flex'
  | 'inline flex'
  | 'block grid'
  | 'inline grid'
  | 'block flow-root'
  | 'inherit'
  | 'initial'
  | 'revert'
  | 'unset';

export interface BoxProps {
  /** The component type to render. Defaults to `div`. */
  as?: string | React.ComponentType;

  /** The display CSS property/ */
  display?: DisplayValue;

  /** The width CSS property */
  width?: Sizing;

  /** The height CSS property */
  height?: Sizing;

  /** All axis margins */
  m?: SizingAny;
  /** X-Axis margins */
  mx?: Sizing2;
  /** Y-Axis margins */
  my?: Sizing2;
  /** Top margin */
  mt?: Sizing;
  /** Right margin */
  mr?: Sizing;
  /** Bottom margin */
  mb?: Sizing;
  /** Left margin */
  ml?: Sizing;

  /** All axis padding */
  p?: SizingAny;
  /** X-Axis padding */
  px?: Sizing2;
  /** Y-Axis padding */
  py?: Sizing2;
  /** Top padding */
  pt?: Sizing;
  /** Right padding */
  pr?: Sizing;
  /** Bottom padding */
  pb?: Sizing;
  /** Left padding */
  pl?: Sizing;

  /** Flex shorthand (flex-grow, flex-shrink, flex-basis) */
  flex?: string;

  /** Flex grow, or `true` for flex-grow: 1 */
  grow?: number | boolean;
  /** Flex shrink, or `true` for flex-shrink: 1 */
  shrink?: number | boolean;
  /** Flex basis */
  basis?: number | string;
  alignSelf?: 'auto' | FlexAlignValue;
  order?: number;
}

export const Box = styled.div<BoxProps>`
  display: ${(props) => props.display};
  width: ${(props) => props.width};
  height: ${(props) => props.height};

  margin-top: ${(props) => resolveSizing(props.mt) || resolveSizing(props.my, 0) || resolveSizing(props.m, 0)};
  margin-right: ${(props) => resolveSizing(props.mr) || resolveSizing(props.mx) || resolveSizing(props.m, 1)};
  margin-bottom: ${(props) => resolveSizing(props.mb) || resolveSizing(props.my) || resolveSizing(props.m, 2, 0)};
  margin-left: ${(props) => resolveSizing(props.ml) || resolveSizing(props.mx) || resolveSizing(props.m, 3, 1)};

  padding-top: ${(props) => resolveSizing(props.pt) || resolveSizing(props.py, 0) || resolveSizing(props.p, 0)};
  padding-right: ${(props) => resolveSizing(props.pr) || resolveSizing(props.px) || resolveSizing(props.p, 1)};
  padding-bottom: ${(props) => resolveSizing(props.pb) || resolveSizing(props.py) || resolveSizing(props.p, 2, 0)};
  padding-left: ${(props) => resolveSizing(props.pl) || resolveSizing(props.px) || resolveSizing(props.p, 3, 1)};

  flex: ${(props) => props.flex};
  flex-grow: ${(props) => (props.grow == null ? undefined : typeof props.grow === 'number' ? props.grow : 1)};
  flex-shrink: ${(props) => (props.shrink == null ? undefined : typeof props.shrink === 'number' ? props.shrink : 1)};
  flex-basis: ${(props) => props.basis};
  align-self: ${(props) => props.alignSelf};
  order: ${(props) => props.order};
`;

Box.defaultProps = {
  as: 'div',
};
